import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { tomorrow, vscDarkPlus } from "react-syntax-highlighter/dist/esm/styles/prism";

/**
 * MarkdownRenderer Component - Clean and consistent markdown rendering
 */
const MarkdownRenderer = ({ content, isDarkMode = true }) => {
  // Process content to ensure it's a clean string
  const processContent = (rawContent) => {
    if (!rawContent) return '';
    
    if (typeof rawContent === 'string') {
      // Clean up problematic characters and formatting
      return rawContent
        // Fix weird Unicode and formatting issues
        .replace(/\u2217/g, '*')  // Replace bullet operator with asterisk
        .replace(/\u2212/g, '-')  // Replace minus sign with hyphen
        .replace(/\u2013/g, '-')  // Replace en dash with hyphen
        .replace(/\u2014/g, '--') // Replace em dash with double hyphen
        // Clean up malformed math expressions
        .replace(/\$\$([^$]*)\$\$/g, '$1') // Remove double dollar signs
        .replace(/\$([^$]*)\$/g, '$1')     // Remove single dollar signs
        // Fix broken bold formatting
        .replace(/\*\*\s*([^*]+?)\s*\*\*/g, '**$1**')
        // Clean up extra whitespace
        .replace(/\s+/g, ' ')
        .trim();
    }
    
    if (Array.isArray(rawContent)) {
      return rawContent
        .map(item => typeof item === 'string' ? item.trim() : String(item))
        .filter(item => item.length > 0)
        .join('\n\n');
    }
    
    return String(rawContent).trim();
  };

  const cleanContent = processContent(content);

  // Enhanced custom components for consistent styling
  const components = {
    // Code blocks and inline code
    code: ({ node, inline, className, children, ...props }) => {
      const match = /language-(\w+)/.exec(className || '');
      const language = match ? match[1] : 'text';

      if (!inline && match) {
        return (
          <div className="my-4">
            <SyntaxHighlighter
              style={isDarkMode ? vscDarkPlus : tomorrow}
              language={language}
              PreTag="div"
              customStyle={{
                borderRadius: '8px',
                fontSize: '14px',
                lineHeight: '1.6',
                padding: '16px',
                margin: 0,
                backgroundColor: isDarkMode ? '#1e1e1e' : '#2d2d2d',
                color: '#ffffff',
              }}
              {...props}
            >
              {String(children).replace(/\n$/, '')}
            </SyntaxHighlighter>
          </div>
        );
      }

      return (
        <code
          className="inline-code"
          style={{
            padding: '0.2em 0.4em',
            borderRadius: '4px',
            fontSize: '0.85em',
            fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace',
            backgroundColor: isDarkMode ? '#374151' : '#E5E7EB',
            color: isDarkMode ? '#ffffff' : '#1f2937',
          }}
          {...props}
        >
          {children}
        </code>
      );
    },

    // Headings
    h1: ({ children, ...props }) => (
      <h1 
        className="text-3xl font-bold mt-8 mb-4"
        style={{ color: isDarkMode ? '#ffffff' : '#1f2937' }}
        {...props}
      >
        {children}
      </h1>
    ),
    h2: ({ children, ...props }) => (
      <h2 
        className="text-2xl font-semibold mt-6 mb-3"
        style={{ color: isDarkMode ? '#ffffff' : '#1f2937' }}
        {...props}
      >
        {children}
      </h2>
    ),
    h3: ({ children, ...props }) => (
      <h3 
        className="text-xl font-semibold mt-5 mb-3"
        style={{ color: isDarkMode ? '#ffffff' : '#1f2937' }}
        {...props}
      >
        {children}
      </h3>
    ),
    h4: ({ children, ...props }) => (
      <h4 
        className="text-lg font-semibold mt-4 mb-2"
        style={{ color: isDarkMode ? '#ffffff' : '#1f2937' }}
        {...props}
      >
        {children}
      </h4>
    ),

    // Paragraphs and text
    p: ({ children, ...props }) => (
      <p 
        className="mb-4 leading-relaxed"
        style={{ color: isDarkMode ? '#ffffff' : '#374151' }}
        {...props}
      >
        {children}
      </p>
    ),

    // Lists
    ul: ({ children, ...props }) => (
      <ul 
        className="list-disc list-inside mb-4 space-y-1"
        style={{ color: isDarkMode ? '#ffffff' : '#374151' }}
        {...props}
      >
        {children}
      </ul>
    ),
    ol: ({ children, ...props }) => (
      <ol 
        className="list-decimal list-inside mb-4 space-y-1"
        style={{ color: isDarkMode ? '#ffffff' : '#374151' }}
        {...props}
      >
        {children}
      </ol>
    ),
    li: ({ children, ...props }) => (
      <li 
        className="leading-relaxed"
        style={{ color: isDarkMode ? '#ffffff' : '#374151' }}
        {...props}
      >
        {children}
      </li>
    ),

    // Links
    a: ({ href, children, ...props }) => (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="underline hover:no-underline transition-all duration-200"
        style={{ color: isDarkMode ? '#60A5FA' : '#2563eb' }}
        {...props}
      >
        {children}
      </a>
    ),

    // Blockquotes
    blockquote: ({ children, ...props }) => (
      <blockquote 
        className="border-l-4 pl-4 py-2 my-4 italic"
        style={{ 
          borderColor: isDarkMode ? '#6B7280' : '#9CA3AF',
          backgroundColor: isDarkMode ? '#374151' : '#F3F4F6',
          color: isDarkMode ? '#ffffff' : '#374151'
        }}
        {...props}
      >
        {children}
      </blockquote>
    ),

    // Tables
    table: ({ children, ...props }) => (
      <div className="overflow-x-auto my-4">
        <table 
          className="min-w-full border-collapse"
          style={{ 
            border: isDarkMode ? '1px solid #6B7280' : '1px solid #D1D5DB',
            color: isDarkMode ? '#ffffff' : '#374151'
          }}
          {...props}
        >
          {children}
        </table>
      </div>
    ),
    thead: ({ children, ...props }) => (
      <thead 
        style={{ 
          backgroundColor: isDarkMode ? '#374151' : '#F9FAFB',
          color: isDarkMode ? '#ffffff' : '#1f2937'
        }}
        {...props}
      >
        {children}
      </thead>
    ),
    th: ({ children, ...props }) => (
      <th 
        className="px-4 py-2 text-left font-semibold"
        style={{ 
          border: isDarkMode ? '1px solid #6B7280' : '1px solid #D1D5DB',
          color: isDarkMode ? '#ffffff' : '#1f2937'
        }}
        {...props}
      >
        {children}
      </th>
    ),
    td: ({ children, ...props }) => (
      <td 
        className="px-4 py-2"
        style={{ 
          border: isDarkMode ? '1px solid #6B7280' : '1px solid #D1D5DB',
          color: isDarkMode ? '#ffffff' : '#374151'
        }}
        {...props}
      >
        {children}
      </td>
    ),

    // Text formatting
    strong: ({ children, ...props }) => (
      <strong 
        className="font-bold"
        style={{ color: isDarkMode ? '#ffffff' : '#1f2937' }}
        {...props}
      >
        {children}
      </strong>
    ),
    em: ({ children, ...props }) => (
      <em 
        className="italic"
        style={{ color: isDarkMode ? '#ffffff' : '#374151' }}
        {...props}
      >
        {children}
      </em>
    ),

    // Images
    img: ({ src, alt, ...props }) => (
      <img
        src={src}
        alt={alt}
        className="max-w-full h-auto rounded-lg shadow-md my-4 mx-auto block"
        {...props}
      />
    ),

    // Horizontal rules
    hr: ({ ...props }) => (
      <hr 
        className="my-8"
        style={{ borderColor: isDarkMode ? '#6B7280' : '#D1D5DB' }}
        {...props}
      />
    ),
  };

  return (
    <div className="markdown-content max-w-none">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={components}
        skipHtml={false}
      >
        {cleanContent}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownRenderer;
